import React from 'react';
import { graphql } from 'gatsby';
import TextComponent from '~/components/reusable/TextComponent';
import {
  getCompleteStyleManualPositioning,
  getSettingsClass,
} from '~/utils/helpers';
import { useViewport } from '~/utils/viewport-provider';
import Checkmarks from '../checkmarks/checkmarks';

const infiniteSignVariations = ['&#8734;', '∞'];

interface IPricingCardComponent {
  theme: string;
  title_text: ReusableTextField;
  subtitle_text: ReusableTextField;
  image: string;
  image_alt_text: string;
  manual_positioning: ManualPositioning;
  blocks: {
    _template: string;
    columns_bootstrap_rule: string;
    checkmarks_align_left: boolean;
    margin_top: number;
    margin_bottom: number;
    gap: string;
    checkmarks: {
      name: string;
      text: string;
    }[];
    title_text: ReusableTextField;
    highlighted_text: ReusableTextField;
    non_highlighted_text: ReusableTextField;
  }[];
}

interface Props {
  block: {
    settings: Settings;
    pricing_cards_v2: IPricingCardComponent[];
  };
}

export function PricingCardsV2({ block }: Props) {
  const { settings } = block;
  const settingsClass = getSettingsClass(settings);

  return (
    <section className={`container ${settingsClass} block__pricing_cards_v2`}>
      {block.pricing_cards_v2.map((card, index) => {
        return (
          <CardComponent
            data={card}
            key={index}
          />
        );
      })}
    </section>
  );
}

function CardComponent({ data }: { data: IPricingCardComponent }) {
  const { isMobile } = useViewport();
  let { mobile, desktop } = getCompleteStyleManualPositioning(
    data.manual_positioning
  );

  return (
    <div className={`${data.theme} pricing-card`}>
      <div className='inner-wrapper'>
        <img
          className='pricing-card__image'
          src={data.image}
          alt={data.image_alt_text}
          title={data.image_alt_text}
          style={isMobile ? mobile : desktop}
        />
        <TextComponent
          data={data.title_text}
          className='highlighted-text title-text'
        />

        <TextComponent
          data={data.subtitle_text}
          className='subtitle-text'
        />

        {data?.blocks?.map((block, index) => {
          switch (block._template) {
            case 'text_point':
              return (
                <TextPoint
                  key={index}
                  title_text={block.title_text}
                  highlighted_text={block.highlighted_text}
                  non_highlighted_text={block.non_highlighted_text}
                />
              );
            case 'block__checkmarks':
              return (
                <Checkmarks
                  key={index}
                  alignLeft={block.checkmarks_align_left}
                  marginTop={block.margin_top}
                  marginBottom={block.margin_bottom}
                  checkmarks={block.checkmarks || []}
                  gap={block.gap}
                  columns_bootstrap_rule={block.columns_bootstrap_rule}
                />
              );
            default:
              return 'Missing block.';
          }
        })}
      </div>
    </div>
  );
}

function TextPoint({
  title_text,
  highlighted_text,
  non_highlighted_text,
}: {
  title_text: ReusableTextField;
  highlighted_text: ReusableTextField;
  non_highlighted_text: ReusableTextField;
}) {
  return (
    <div className='my-[23px]'>
      <TextComponent
        data={title_text}
        as='p'
        className='mb-1'
      />
      <div>
        <TextComponent
          data={highlighted_text || {}}
          as='span'
          className={`${highlighted_text?.input_font_color ? '' : 'highlighted-text'} ${infiniteSignVariations.includes(highlighted_text?.input_text) ? 'has-infinite-sign' : ''} text-point`}
        />{' '}
        <TextComponent
          data={non_highlighted_text || {}}
          as='span'
          className='non-highlighted-text'
        />
      </div>
    </div>
  );
}

export const pricingFragment = graphql`
  fragment PricingCardsV2Fragment on Block {
    pricing_cards_v2 {
      theme
      title_text {
        ...ReusableTextFieldFragment
      }
      subtitle_text {
        ...ReusableTextFieldFragment
      }
      image
      image_alt_text
      manual_positioning {
        ...ManualPositioningFragment
      }
      blocks {
        _template
        columns_bootstrap_rule
        checkmarks_align_left
        margin_top
        margin_bottom
        gap
        checkmarks {
          name
          text
        }
        title_text {
          ...ReusableTextFieldFragment
        }
        highlighted_text {
          ...ReusableTextFieldFragment
        }
        non_highlighted_text {
          ...ReusableTextFieldFragment
        }
      }
    }
  }
`;
